// LOOP AGENDA MADRE WORKER PROFILE PARITY V93 — view config unchanged; receives profile from data layer.
// LOOP AGENDA MADRE WORKER ACCESS MIRROR V86 — resource view config unchanged; receives enriched worker profile from data layer.
// LOOP AGENDA MADRE WORKER RESOURCE PARITY V84 — resource photo contract preserved for owner/worker/native parity.
 // LOOP AGENDA MADRE WORKER PERMISSIONS EXACT V80 — resource photos use the same broad worker-image contract as AdminBookingCalendar.
// LOOP AGENDA MADRE CLEANUP D7 V78 — responsive cleanup validated; data/view contracts unchanged.
// LOOP AGENDA MADRE CLEANUP D6 V77 — compact duplicate event-card/toolbar CSS after D5 validation.
// LOOP AGENDA MADRE CLEANUP D5 V76 — agenda data/view config unchanged; paired with native toolbar cleanup.
// src/adminCalendar/nativeUi/useAdminCalendarViewConfig.js
// LOOP AGENDA MADRE CLEANUP D3 V74 — view contract remains custom for Week/Month and resource-based for Day.
// LOOP AGENDA MADRE CLEANUP D2 V73 — custom view contract unchanged; cleanup removed only dead visual surfaces.
// LOOP AGENDA MADRE CLEANUP D1 V72 — custom week/month contracts also apply to native config.
// LOOP AGENDA MADRE PHASE C V71 — month mode also stays in the single custom agenda renderer.
// LOOP AGENDA MADRE PHASE B V69 — week mode stays in the single custom agenda renderer.
// LOOP AGENDA MADRE PHASE A V67 — view config supports one day/resource visual contract.
// IMPORTANT: save as UTF-8 without BOM.

import { useMemo } from "react";

function resourcePhotoFromEmployee(e = {}) {
  return (
    e.photoUrl ||
    e.photoURL ||
    e.photo_url ||
    e.photo ||
    e.image ||
    e.imageUrl ||
    e.imageURL ||
    e.avatar ||
    e.avatarUrl ||
    e.avatarURL ||
    e.profileImage ||
    e.profileImageUrl ||
    e.profilePhoto ||
    e.profilePhotoUrl ||
    e.picture ||
    e.pictureUrl ||
    e.pictureURL ||
    e.workerPhoto ||
    e.workerPhotoUrl ||
    e.employeePhoto ||
    e.employeePhotoUrl ||
    e.publicPhotoUrl ||
    e.userPhotoURL ||
    e.userPhotoUrl ||
    null
  );
}

export default function useAdminCalendarViewConfig({
  isNativeRuntime,
  viewMode,
  businessDoc,
  isWorkerView,
  employees,
  selectedEmployeeIds,
  allEvents,
  eventsForDay,
  selectedSet,
}) {
  function resourcesFromEmployees(empList, selIds) {
    const sel = new Set((selIds || []).map(String));
    return (empList || [])
      .filter((e) => (selIds && selIds.length > 0 ? sel.has(String(e.id)) : true))
      .map((e) => ({
        id: String(e.id),
        title: e.name || e.displayName || "Sin nombre",
        name: e.name || e.displayName || "Sin nombre",
        displayName: e.displayName || e.name || "Sin nombre",
        role: e.role || e.position || e.specialty || e.title || "",
        photoUrl: resourcePhotoFromEmployee(e),
        photo: resourcePhotoFromEmployee(e),
        image: resourcePhotoFromEmployee(e),
        avatarUrl: resourcePhotoFromEmployee(e),
      }));
  }

  // LOOP AGENDA MADRE PHASE A V67
  // Native no longer switches to a separate single-day agenda when only one
  // resource is visible. Day uses the same resource grid everywhere.
  const nativeShouldUseResourceColumns = useMemo(() => {
    if (!isNativeRuntime) return false;
    if (viewMode !== "day") return false;
    return Array.isArray(employees) && employees.length > 0;
  }, [isNativeRuntime, viewMode, employees]);

  const { calendarView, calendarResources } = useMemo(() => {
    // NATIVE
    if (isNativeRuntime) {
      if (viewMode === "weekCompany") return { calendarView: "loopWeek", calendarResources: [] };
      if (viewMode === "month") return { calendarView: "loopMonth", calendarResources: [] };
      if (nativeShouldUseResourceColumns) {
        return {
          calendarView: "resourceTimeGridDay",
          calendarResources: resourcesFromEmployees(employees, selectedEmployeeIds),
        };
      }
      return { calendarView: "timeGridDay", calendarResources: [] };
    }

    // WEB
    // LOOP AGENDA MADRE PHASE B/C:
    // Week and Month are rendered by the custom mother agenda router, not by FullCalendar.
    // Keep resources empty here to avoid resurrecting a second visual calendar.
    if (viewMode === "weekCompany") return { calendarView: "loopWeek", calendarResources: [] };
    if (viewMode === "month") return { calendarView: "loopMonth", calendarResources: [] };

    const hasEmployees = Array.isArray(employees) && employees.length > 0;
    const companyNoWorkerMode = Boolean((businessDoc?.noWorkers === true || businessDoc?.noWorkerMode === true) && !hasEmployees);

    const cv = companyNoWorkerMode ? "timeGridDay" : viewMode === "day" ? "resourceTimeGridDay" : viewMode === "weekCompany" ? "loopWeek" : viewMode === "month" ? "loopMonth" : isWorkerView ? "loopWeek" : "resourceTimeGridDay";

    const cr = companyNoWorkerMode ? [] : resourcesFromEmployees(employees, selectedEmployeeIds);

    return { calendarView: cv, calendarResources: cr };
  }, [
    isNativeRuntime,
    nativeShouldUseResourceColumns,
    employees,
    selectedEmployeeIds,
    viewMode,
    businessDoc,
    isWorkerView,
  ]);

  const calendarEvents = useMemo(() => {
    if (isNativeRuntime) {
      if (viewMode === "weekCompany" || viewMode === "month") {
        const base = allEvents || [];
        if (selectedEmployeeIds && selectedEmployeeIds.length > 0) {
          return base.filter((ev) => {
            const rid = ev.resourceId ? String(ev.resourceId) : "";
            if (!rid) return true;
            return selectedSet.has(rid);
          });
        }
        return base;
      }

      // if user selected some workers, filter to those workers
      if (selectedEmployeeIds && selectedEmployeeIds.length > 0) {
        return (allEvents || []).filter((ev) => {
          const rid = ev.resourceId ? String(ev.resourceId) : "";
          if (!rid) return true;
          return selectedSet.has(rid);
        });
      }
      return eventsForDay || [];
    }

    if (viewMode === "weekCompany") return allEvents || [];
    if (viewMode === "month") return allEvents || [];
    if (isWorkerView) return allEvents || [];
    return eventsForDay || [];
  }, [isNativeRuntime, selectedEmployeeIds, allEvents, eventsForDay, selectedSet, viewMode, isWorkerView]);

  return { nativeShouldUseResourceColumns, calendarView, calendarResources, calendarEvents };
}